
/* ============================== src/auth/AuthProvider.jsx ============================== */
// path: frontend/src/auth/AuthProvider.jsx
/**
 * AuthProvider: единственная точка авторизации.
 *
 * - GET /auth/me при монтировании (useQuery(['me'])).
 * - login(identifier, password) → POST /auth/login + invalidateQueries(['me']).
 * - logout() → POST /auth/logout + сброс кэша текущего пользователя.
 *
 * Возвращает { user, capabilities, isLoading, forceChangePassword,
 * passwordStatus, login, logout }.
 *
 * capabilities — плоский Record<Capability, boolean> с бэка
 * (domain/permissions.ts::computeCapabilities по effective-сотруднику).
 * Фронт не реализует hasRole-логику: только читает флаги. Заменяет
 * удалённый utils/permissions.js.
 *
 * forceChangePassword = true, если пароль истек или требуется принудительная смена.
 */
import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../api/client.js";
import { AuthContext } from "./AuthContext";

const EMPTY_CAPABILITIES = Object.freeze({});

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();

  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        const data = await api.get("/auth/me");
        return data;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          return null;
        }
        throw err;
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const login = useCallback(
    async (identifier, password) => {
      const data = await api.post("/auth/login", {
        email: identifier,
        password,
      });
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      return data;
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => null);

    queryClient.setQueryData(["me"], null);

    queryClient.removeQueries({
      predicate: (query) => query.queryKey[0] !== "me",
    });
  }, [queryClient]);

  const value = useMemo(
    () => ({
      user: meQuery.data?.employee ?? null,
      capabilities: meQuery.data?.capabilities ?? EMPTY_CAPABILITIES,
      isLoading: meQuery.isLoading,
      forceChangePassword: meQuery.data?.forceChangePassword ?? false,
      passwordStatus: meQuery.data?.passwordStatus ?? null,
      login,
      logout,
    }),
    [meQuery.data, meQuery.isLoading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
